import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { datePickerStyle, advancedSettingsSectionStyle, toggleButtonStyle } from '@bingads-webui-campaign-react/common-styles';
import { creationCommonStyle } from '@bingads-webui-campaign-react/campaign-adgroup-settings-shared-styles';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ChoiceGroup, Label } from '@fluentui/react';
import { toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { DatePickerBasic } from './date-picker-basic';

const getStyles = (__, core) =>
  ({
    '@global': {
      ...datePickerStyle(core),
    },
    dateSettingContainer: {
      ...creationCommonStyle(core),
      ...advancedSettingsSectionStyle(core),
      ...toggleButtonStyle(core),
      '& .date-settings-intro': {
        paddingLeft: 0,
      },
      '& .date-settings': {
        '& .ms-ChoiceField': {
          '& .ms-ChoiceField-input': {
            position: 'absolute',
            opacity: 0,
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            margin: 0,
          },
          '& .ms-ChoiceField-field': {
            padding: 0,
            marginRight: 8,
            '& .ms-ChoiceFieldLabel': {
              verticalAlign: 'baseline',
              fontWeight: 400,
            },
          },
        },
        '& .date-content': {
          width: 140,
        },
        '& .end-by-container': {
          display: 'flex',
          alignItems: 'center',
          width: 220,
        },
      },
      '& .alert-danger': {
        backgroundColor: 'unset',
        color: '#A80000',
      },
    },
  });

export const DateSetting = (props) => {
  const {
    label,
    data,
    i18n,
    adGroupStatus,
    onChange,
    activityLogger,
  } = props;
  const classes = useDefaultStyles(getStyles);
  const currentDate = useMemo(() => {
    const localDate = new Date(new Date().toLocaleString('en-US', { timeZone: toIANATimeZone(data.TimeZone) }));
    return new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0);
  }, [data.TimeZone]);
  const [savedStartDate, setSavedStartDate] = useState();
  const rawStartDate = useMemo(() => data.StartDate || currentDate, [currentDate, data.StartDate]);
  const rawEndDate = useMemo(() => {
    const localDate = new Date(new Date().toLocaleString('en-US', { timeZone: toIANATimeZone(data.TimeZone) }));
    const nextYearDateWithTimeZone = new Date(localDate.getFullYear() + 1, localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0);
    return data.EndDate || nextYearDateWithTimeZone;
  }, [data.EndDate, data.TimeZone]);
  const hasFixedStartDate = useMemo(() => !((savedStartDate > currentDate) || adGroupStatus === 'Draft'), [adGroupStatus, currentDate, savedStartDate]);
  const [hasEndDate, setHasEndDate] = useState(() => (!!data.EndDate));
  const onFormatDate = useCallback(date => (!date ? '' : i18n.formatDate(date)), [i18n]);
  const onStartDateChange = useCallback((date) => {
    if (activityLogger) {
      activityLogger.signal('start date changed', 'ad schedule');
    }
    onChange({
      StartDate: date,
      EndDate: hasEndDate ? rawEndDate : null,
      hasFixedStartDate,
    });
  }, [activityLogger, hasEndDate, hasFixedStartDate, onChange, rawEndDate]);

  const onEndDateChange = useCallback((date) => {
    if (activityLogger) {
      activityLogger.signal('end date changed', 'ad schedule');
    }
    onChange({
      StartDate: rawStartDate,
      // eslint-disable-next-line no-nested-ternary
      EndDate: hasEndDate ? date : null,
      hasFixedStartDate,
    });
  }, [activityLogger, hasEndDate, hasFixedStartDate, onChange, rawStartDate]);

  const options = useMemo(() => [
    {
      key: 'false',
      text: i18n.getString(_TL_('No end date')),
      ariaLabel: i18n.getString(_TL_('No end date')),
    },
    {
      key: 'true',
      text: i18n.getString(_TL_('End by')),
      ariaLabel: i18n.getString(_TL_('End by')),
      onRenderField: (fieldProps, render) => (
        <div className="end-by-container">
          <div>
            {render(fieldProps)}
          </div>
          <div>
            <DatePickerBasic
              className="end-date-content"
              ariaLabel="end-date-picker"
              disabled={!hasEndDate}
              dateValue={rawEndDate}
              onFormatDate={onFormatDate}
              onDateChange={onEndDateChange}
              i18n={i18n}
            />
          </div>
        </div>
      ),
    },
  ], [hasEndDate, onEndDateChange, onFormatDate, rawEndDate, i18n]);

  const onEndDateChoiceChange = useCallback((ev, option) => {
    const newChoiceValue = option.key;
    setHasEndDate(newChoiceValue === 'true');

    if (activityLogger) {
      activityLogger.signal(`has end date change: ${newChoiceValue}`, 'ad schedule');
    }
    onChange({
      StartDate: rawStartDate,
      EndDate: newChoiceValue === 'true' ? rawEndDate : null,
      hasFixedStartDate,
    });
  }, [activityLogger, hasFixedStartDate, onChange, rawEndDate, rawStartDate]);

  useEffect(() => {
    if (!!data.EndDate !== hasEndDate) {
      setHasEndDate(data.EndDate);
    }
  }, [data.EndDate, hasEndDate]);

  useEffect(() => {
    if (data.StartDate && !savedStartDate) {
      setSavedStartDate(data.StartDate);
    }
  }, [data.StartDate, savedStartDate]);

  return (
    <div
      className={classnames(classes.dateSettingContainer)}
    >
      <div className="date-settings-intro col-sm-12 row">
        <span>{label || i18n.getString(_TL_('How long do you want your ads to run?'))}</span>
      </div>
      <div className="form-horizontal date-settings">
        <Label>{i18n.getString(_TL_('Start date'))}</Label>
        <DatePickerBasic
          className="start-date-content"
          disabled={hasFixedStartDate}
          dateValue={rawStartDate}
          onFormatDate={onFormatDate}
          onDateChange={onStartDateChange}
          ariaLabel="start-date-picker"
          i18n={i18n}
        />
        <ChoiceGroup
          selectedKey={hasEndDate ? 'true' : 'false'}
          options={options}
          label={i18n.getString(_TL_('End date'))}
          onChange={onEndDateChoiceChange}
        />
      </div>
    </div>
  );
};

DateSetting.propTypes = {
  label: PropTypes.string,
  data: PropTypes.shape({
    StartDate: PropTypes.string,
    EndDate: PropTypes.string,
    TimeZone: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }).isRequired,
  adGroupStatus: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func,
  }),
};

DateSetting.defaultProps = {
  label: '',
  activityLogger: null,
  adGroupStatus: undefined,
};
